/**
 * DemoProfileReviewCard — 2026-06-06 source-of-truth-full-ui follow-up.
 *
 * Onboarding Review card. Renders the canonical six demo-user fields
 * sourced from /api/v1/user/portfolio.demo_profile (see backend
 * routes/user_portfolio.py). Missing values render "Not provided" —
 * we never invent data, never carry simulation account fields, never
 * print secrets.
 *
 * Six rows (in order):
 *   First name, Last name, Email, Demo mode, Created, Account ID
 *
 * Each row has a stable data-testid for static bundle tests:
 *   demo-review-first-name / demo-review-last-name / demo-review-email
 *   demo-review-demo-mode  / demo-review-created   / demo-review-account-id
 */
import React from 'react';
import { User as UserIcon } from 'lucide-react';
import useAccountSummary, { naOrValue } from '../hooks/useAccountSummary';

function _fmtCreated(iso: string | null | undefined): string {
  if (!iso) return 'Not provided';
  const stamp = String(iso).endsWith('Z') ? iso : iso.replace(' ', 'T') + 'Z';
  const ts = Date.parse(stamp);
  if (!Number.isFinite(ts)) return 'Not provided';
  // Render as YYYY-MM-DD HH:MM UTC so it stays unambiguous in the UI.
  const d = new Date(ts);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())} UTC`;
}

function _row(testId: string, label: string, value: string) {
  return (
    <div
      data-testid={testId}
      className="flex items-center justify-between py-2 border-b border-[var(--card-border)] last:border-b-0"
    >
      <span className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest">
        {label}
      </span>
      <span className="text-xs font-black text-[var(--app-text)] tracking-tight">
        {value}
      </span>
    </div>
  );
}

export default function DemoProfileReviewCard() {
  const { demoProfile, authenticated, loading } = useAccountSummary();

  return (
    <div
      data-testid="demo-profile-review-card"
      className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-sm"
    >
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center text-emerald-600">
          <UserIcon size={18} />
        </div>
        <h3 className="text-lg font-black text-[var(--app-text)] uppercase italic tracking-tight">
          Onboarding Review
        </h3>
      </div>
      <p className="text-xs text-[var(--muted-text)] font-medium mb-4">
        Prefilled from the demo data Adversyn already collected. Missing
        fields show "Not provided" — Adversyn never invents data.
      </p>

      <div className="space-y-0">
        {_row('demo-review-first-name', 'First name', naOrValue(demoProfile.firstName))}
        {_row('demo-review-last-name', 'Last name', naOrValue(demoProfile.lastName))}
        {_row('demo-review-email', 'Email', naOrValue(demoProfile.email))}
        {_row('demo-review-organization', 'Organization', naOrValue(demoProfile.organization))}
        {_row('demo-review-demo-mode', 'Demo mode',
          demoProfile.demoMode ? 'Enabled' : (loading || !authenticated ? 'Not provided' : 'Disabled'))}
        {_row('demo-review-created', 'Created', _fmtCreated(demoProfile.createdAtUtc))}
        {_row('demo-review-account-id', 'Account ID', naOrValue(demoProfile.userIdMasked))}
      </div>

      <div className="mt-4 text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest opacity-70">
        Source: /api/v1/user/portfolio · demo_profile block
      </div>
    </div>
  );
}
